import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import Header from '../../../common/Header/Header';
import Button from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';
import Table from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useCompareRecipeDetails } from '../../../services/recipe/recipe.service';
import { ComparePage, InputButtonWrapper, InputWrapper } from './CompareRecipes.styles';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { CompareRecipeDetails } from '../../../services/recipe/recipe.types';


export const CompareRecipes: FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [specOneNo, setSpecOneNo] = useState('');
  const [specTwoNo, setSpecTwoNo] = useState('');
  const [specOneNoError, setSpecOneNoError] = useState('');
  const [specTwoNoError, setSpecTwoNoError] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [isClear, setIsClear] = useState(false);
  const [specOneDetailsDataState ,setSpecOneDetailsDataState ] = useState<CompareRecipeDetails[]>([]);
  const [specTwoDetailsDataState,setSpecTwoDetailsDataState ] = useState<CompareRecipeDetails[]>([]);


  const {
    data: specOneDetailsData,
    isError: specOneDetailsDataError,
    remove: removeSpecOneDetails,
  } = useCompareRecipeDetails(specOneNo, specTwoNo,searchActive);

  useFetchErrorToast([{ isError: specOneDetailsDataError, label: 'Error fetching '+specOneNo+' compare Details', id: 'compareRecipes1' }]);
  const {
    data: specTwoDetailsData,
    isError: specTwoDetailsDataError,
    remove: removeSpecTwoDetails,
  } = useCompareRecipeDetails(specTwoNo, specOneNo,searchActive);
  useFetchErrorToast([{ isError: specTwoDetailsDataError, label: 'Error fetching '+specTwoNo+' compare Details', id: 'compareRecipes2' }]);

  const handleSpecOneInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    if (specOneNoError) setSpecOneNoError('');
    setSpecOneNo(event.target.value);
    setSearchActive(false);
    setIsClear(false);
  };

  const handleSpecTwoInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (specTwoNoError) setSpecTwoNoError('');
    setSpecTwoNo(event.target.value);
    setSearchActive(false);
    setIsClear(false);
  };

  useEffect(() => {
    if (specOneNo !== '' && specOneDetailsDataError) {
      setSpecOneNoError('Spec not found');
      setIsClear(true);
    }
    removeSpecOneDetails();
    setSearchActive(false);
  }, [specOneDetailsDataError]);

  useEffect(() => {
    if (specTwoNo !== '' && specTwoDetailsDataError) {
      setSpecTwoNoError('Spec not found');
      setIsClear(true);
    }
    removeSpecTwoDetails();
    setSearchActive(false);
  }, [specTwoDetailsDataError]);

  useEffect(() => {
    if (specOneDetailsData?.data) {
      setSpecOneDetailsDataState(specOneDetailsData.data);
    }
  }, [specOneDetailsData]);

  useEffect(() => {
    if (specTwoDetailsData?.data) {
      setSpecTwoDetailsDataState(specTwoDetailsData.data);
    }
  }, [specTwoDetailsData]);


  const handleCompareClick = () => {

    if (specOneNo !== '' && specTwoNo !== '') {
      setSearchActive(true);
      setIsClear(true);
    }
    if (specOneNo === '') {
      setSpecOneNoError('Spec required');
      setSearchActive(false);
    }
    if (specTwoNo === '') {
      setSpecTwoNoError('Spec required');
      setSearchActive(false);
    }
    if (specOneNo === specTwoNo) {
      setSearchActive(false);
      showToast({
        label: 'Please select two different specs to compare.',
        id: 'compareRecipesMatchError',
        toastType: ToastTypes.Error,
      });
    }
  };

  const handleClearClick = () => {
    setIsClear(false);
    setSearchActive(false);
    setSpecOneNo('');
    setSpecTwoNo('');
    setSpecOneNoError('');
    setSpecTwoNoError('');
    setSpecOneDetailsDataState([]);
    setSpecTwoDetailsDataState([]);
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleCompareClick();
    }
  };

  return (
    <>
      <Header breadcrumb={{ label: 'Back To All Recipes', to: '/recipes' }} title='Compare Recipes' />
      <ComparePage>
        <InputButtonWrapper>
          <InputWrapper>
            <Input
              label='Spec 1'
              id='specOneNo'
              testId='specOneNo'
              value={specOneNo}
              onChange={handleSpecOneInputChange}
              errorText={specOneNoError}
              ref={inputRef}
              onKeyDown={handleKeyDown}
            />
            <Input
              label='Spec 2'
              id='specTwoNo'
              testId='specTwoNo'
              value={specTwoNo}
              onChange={handleSpecTwoInputChange}
              errorText={specTwoNoError}
              onKeyDown={handleKeyDown}
            />
            <Button label={isClear ? 'Clear' : 'Compare'} onClick={isClear ? handleClearClick : handleCompareClick} />
          </InputWrapper>
        </InputButtonWrapper>
        <Table
          showHeader={true}
          headerTitle={specOneDetailsDataState?.length !== 0 ? `Spec ${specOneDetailsDataState[0]?.specNo}` : 'Spec'}
          rowData={specOneDetailsDataState??[]}
          columnDefs={[
            { flex: 1, field: 'specNo', headerName: 'Spec' },
            { flex: 1, field: 'moaClassNoAndDesc', headerName: 'Class' },
            { flex: 1, field: 'moaBaseNoAndDesc', headerName: 'Base' },
            { flex: 1, field: 'moaSizeNoAndDesc', headerName: 'Size' },
            { flex: 1, field: 'moaProductNoAndDesc', headerName: 'Product' },
            { flex: 1, field: 'moaRawProdNoAndDesc', headerName: 'Raw Product' },
            { flex: 1, field: 'usageAmount', headerName: 'Usage Amount' },
          ]}
          returnedRecords={specOneDetailsData?.data.length || 0}
          height={'600px'}
          width={'100%'}
        />

        <Table
          showHeader={true}
          headerTitle={specTwoDetailsDataState?.length !== 0 ? `Spec ${specTwoDetailsDataState[0]?.specNo}` : 'Spec'}
          rowData={specTwoDetailsDataState??[]}
          columnDefs={[
            { flex: 1, field: 'specNo', headerName: 'Spec' },
            { flex: 1, field: 'moaClassNoAndDesc', headerName: 'Class' },
            { flex: 1, field: 'moaBaseNoAndDesc', headerName: 'Base' },
            { flex: 1, field: 'moaSizeNoAndDesc', headerName: 'Size' },
            { flex: 1, field: 'moaProductNoAndDesc', headerName: 'Product' },
            { flex: 1, field: 'moaRawProdNoAndDesc', headerName: 'Raw Product' },
            { flex: 1, field: 'usageAmount', headerName: 'Usage Amount' },
          ]}
          returnedRecords={specTwoDetailsData?.data.length || 0}
          height={'600px'}
          width={'100%'}
        />
      </ComparePage>
    </>
  );
};

export default CompareRecipes;